import { useState, useCallback } from "react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { Input } from "@/components/ui/input";
import { Dialog } from "@/components/shared/dialog";
import { SubTopic } from "@/v2/entities/subtopic/SubTopic.model";
import { useSubTopicApi } from "@/v2/entities/subtopic/SubTopic.api";
import { Loader2Icon } from "lucide-react";
import { toast } from "sonner";

interface EditSubTopicDialogProps {
  subTopic: SubTopic;
  onSuccess: () => void;
  onCancel: () => void;
}

export function EditSubTopicDialog({
  subTopic,
  onSuccess,
  onCancel,
}: EditSubTopicDialogProps) {
  const [name, setName] = useState(subTopic.name);
  const [isLoading, setIsLoading] = useState(false);
  const subTopicApi = useSubTopicApi();

  const handleSave = useCallback(async () => {
    if (!name.trim()) return;
    setIsLoading(true);
    try {
      await subTopicApi.updateSubTopic(subTopic.id, { name: name.trim() });
      toast.success("SubTopic updated successfully");
      onSuccess();
    } catch {
      toast.error("Failed to update subtopic");
    } finally {
      setIsLoading(false);
    }
  }, [name, subTopic.id, subTopicApi, onSuccess]);

  return (
    <Dialog.Content size="small">
      <Flex direction="column" gap="4" p="4">
        <Dialog.Title>Edit SubTopic</Dialog.Title>
        <Flex direction="column" gap="2">
          <Text size="2" weight="medium">
            Name
          </Text>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="SubTopic name"
          />
        </Flex>
        <Flex gap="3" justify="end">
          <Button
            variant="soft"
            color="gray"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!name.trim() || isLoading}
            highContrast
          >
            {isLoading ? <Loader2Icon className="animate-spin" /> : "Save"}
          </Button>
        </Flex>
      </Flex>
    </Dialog.Content>
  );
}

